import { isEmpty } from 'lodash';
import { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router';
import { toast } from 'react-toastify';
import PageSpinner from '../components/shared/PageSpinner';
import { Footer } from '../layout';
import type { ITermsConditionData } from '../models/login';
import MarketingConfirmationModal from '../pages/profile/MarketingConfirmationModal';
import MarketingConsentModal from '../pages/profile/MarketingConsentModal';
import TermsAndConditionModal from '../pages/terms-conditions/TermsAndConditionModal';
import {
  getTermAndCondition,
  saveMarketingPreferenceForUser,
  saveTermsAndCondition,
} from '../services/login/loginApi-service';
import { getProfile } from '../services/user/user-service';
import { userDetail } from '../store/actions/app.Actions';
import type { AppState } from '../store/state/app.state';
import type { IMainLayoutProps } from '../types/propTypes';
import type { ITermsConditionModalState } from '../types/stateTypes';
import { Common } from '../utils/constants';
import Header from './header';

const MainLayout = ({ logout }: IMainLayoutProps) => {
  const userData = useSelector((state: AppState) => state.UserData);
  const [termsAndConditionData, setTermsAndConditionData] = useState<ITermsConditionData>();

  const dispatch = useDispatch();

  const [termsModal, setTermsModal] = useState<ITermsConditionModalState>({
    showModal: false,
    body: '',
    callBack: null,
  });

  const [marketingConsentModal, setMarketingConsentModal] = useState({
    showModal: false,
    message: '',
  });

  const [marketingConfirmationModal, setMarketingConfirmationModal] = useState({
    showModal: false,
    message: '',
  });

  const isTermsAndConditionRequired = userData?.terms_and_condition_required;
  const isMarketingConsentRequired =
    userData?.marketing_status_code !== Common.MarketingStatusCode.UNSUBSCRIBED_MAILCHIMP &&
    userData?.show_consent_prompt;

  useEffect(() => {
    fetchData();
  }, []);

  const closeModal = () => {
    setTermsModal({
      showModal: false,
      body: '',
      callBack: null,
    });
  };

  const fetchData = async () => {
    try {
      const response = await getTermAndCondition();
      setTermsAndConditionData(response.Data);
      if (isTermsAndConditionRequired) {
        setTermsModal({
          showModal: true,
          body: response.Data.content,
          callBack: () => saveTermsAndConditions(response.Data.id),
        });
        return;
      }
      if (isMarketingConsentRequired) {
        setMarketingConsentModal({
          showModal: true,
          message: userData.marketing_consent_prompt_message,
        });
      }
    } catch (error) {
      toast.error(error.response?.message || error.toString());
    }
  };

  const saveTermsAndConditions = async (termsAndConditionId: number) => {
    try {
      const input = {
        terms_conditions: [{ id: termsAndConditionId, accepted: true }],
      };
      await saveTermsAndCondition(input);
      closeModal();

      if (isMarketingConsentRequired) {
        setMarketingConsentModal({
          showModal: true,
          message: userData.marketing_consent_prompt_message,
        });
      }
    } catch (error) {
      toast.error(error.response?.message || error.toString());
    }
  };

  const getUserData = async () => {
    try {
      const response = await getProfile();
      dispatch(userDetail(response.Data));
    } catch (error) {
      toast.error(error.toString());
    }
  };

  const saveMarketingPreference = async (isMarketingConsentAccepted: boolean) => {
    try {
      const input = {
        is_marketing_consent_accepted: isMarketingConsentAccepted,
      };
      const response = await saveMarketingPreferenceForUser(input);

      setMarketingConsentModal({
        showModal: false,
        message: '',
      });

      if (isEmpty(response?.Data)) {
        await getUserData();
        return;
      }

      setMarketingConfirmationModal({
        showModal: true,
        message: String(response.Data),
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || error.toString());
    }
  };

  const closeMarketingConfirmationModal = async () => {
    setMarketingConfirmationModal({
      showModal: false,
      message: '',
    });
    await getUserData();
  };

  useEffect(() => {
    if (document.getElementById(window.location.pathname.trim())) {
      const oldActiveElements = document.querySelectorAll('.nav-item.active');
      if (oldActiveElements.length) {
        for (const element of oldActiveElements) {
          element.classList.remove('active');
        }
      }

      document.getElementById(window.location.pathname.trim())?.classList.add('active');
    } else {
      const oldActiveElement = document.querySelector('.active');
      if (oldActiveElement) {
        oldActiveElement.classList.remove('active');
      }
    }
  }, []);

  return (
    <div className="wrapper">
      <Header logout={logout} termsAndConditionModalContent={termsAndConditionData?.content} />
      <main className="main-content">
        <div className="container-xl">
          <div className="card mb-4">
            <Suspense fallback={<PageSpinner />}>
              <Outlet />
            </Suspense>
          </div>
        </div>
      </main>
      <Footer />

      {termsModal.showModal && (
        <TermsAndConditionModal modal={termsModal} isShowCloseButton={false} isShowAccceptButton={true} />
      )}

      {marketingConsentModal.showModal && (
        <MarketingConsentModal
          showMarketingPreferencePopup={marketingConsentModal.showModal}
          marketingConsentPrompt={marketingConsentModal.message}
          callBack={saveMarketingPreference}
        />
      )}

      {marketingConfirmationModal.showModal && (
        <MarketingConfirmationModal
          showModal={marketingConfirmationModal.showModal}
          closeModal={closeMarketingConfirmationModal}
          confirmationPrompt={marketingConfirmationModal.message}
        />
      )}
    </div>
  );
};

export default MainLayout;
